import { useState, type FormEvent } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { footerColumns, type FooterColumn } from '~/content/navigation';
import { Container, Grid } from '~/components/ui';
import { cn } from '~/lib/cn';
import {
  isLocalizedCanonicalPath,
  LOCALIZED_LOCALES,
  localizedLocaleFromPathname,
  localizedPath,
  normalizeCanonicalPath,
} from '~/lib/localized-routes';

// Section header = 15px token; links = 17px semibold token. Hover dims the
// other links in the same column (group/col) while the hovered one stays
// black — matching the mega menu.
const titleClass = 'text-[15px] font-medium leading-[1.32] tracking-[0.27px] text-grey-700';
const linkClass =
  'whitespace-nowrap py-3 text-[17px] leading-[1.16] font-semibold tracking-[-0.034px] text-black transition-colors ease-nav duration-150 group-has-[a:hover]/col:text-grey-500 hover:!text-black';

function FooterNav({ column }: { column: FooterColumn }) {
  return (
    <nav aria-label={column.title} className="group/col flex flex-col">
      <p className={titleClass}>{column.title}</p>
      <div className="mt-3 -mb-3 flex flex-col">
        {column.links.map((link) =>
          link.external ? (
            <a
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}>
              {link.label}
            </a>
          ) : (
            <Link key={link.label} to={link.href} className={linkClass}>
              {link.label}
            </Link>
          ),
        )}
      </div>
    </nav>
  );
}

const localeLabels = {
  'en-bd': 'Bangladesh',
  'en-pk': 'Pakistan',
  'en-eg': 'Egypt',
} as const;

/**
 * Server-rendered locale links for every route that existed in Webflow
 * Localization. These are ordinary anchors so crawlers and no-JS visitors can
 * discover each reciprocal regional URL directly from the page body.
 */
function LocaleSelector() {
  const pathname = useRouterState({ select: ({ location }) => location.pathname });
  const activeLocale = localizedLocaleFromPathname(pathname);
  const canonicalPath = normalizeCanonicalPath(
    activeLocale ? pathname.slice(activeLocale.length + 1) || '/' : pathname,
  );

  if (!isLocalizedCanonicalPath(canonicalPath)) return null;

  const localeLinks = [
    { label: 'English', href: canonicalPath, hrefLang: 'en', active: !activeLocale },
    ...LOCALIZED_LOCALES.map((locale) => ({
      label: localeLabels[locale],
      href: localizedPath(canonicalPath, locale),
      hrefLang: locale,
      active: activeLocale === locale,
    })),
  ];

  return (
    <nav aria-label="Regional versions" className="mt-16 border-t border-black-6 pt-8">
      <p className={titleClass}>Regional versions</p>
      <div className="mt-3 flex flex-wrap gap-x-7 gap-y-1">
        {localeLinks.map((link) => (
          <a
            key={link.hrefLang}
            href={link.href}
            hrefLang={link.hrefLang}
            aria-current={link.active ? 'page' : undefined}
            className={cn(
              'py-2 text-[16px] leading-[1.3] transition-colors ease-nav duration-150 hover:text-black',
              link.active ? 'font-semibold text-black' : 'text-grey-700',
            )}>
            {link.label}
          </a>
        ))}
      </div>
    </nav>
  );
}

export function Footer({ className }: { className?: string } = {}) {
  const [email, setEmail] = useState('');
  const [subscription, setSubscription] = useState<{
    state: 'idle' | 'submitting' | 'success' | 'error';
    message?: string;
  }>({ state: 'idle' });

  const onSubscribe = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubscription({ state: 'submitting' });

    const form = new FormData(event.currentTarget);

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          company: form.get('company'),
        }),
      });
      const result = (await response.json().catch(() => null)) as {
        ok?: boolean;
        message?: string;
      } | null;

      if (!response.ok || !result?.ok) {
        setSubscription({
          state: 'error',
          message: result?.message ?? 'We could not subscribe you. Please try again.',
        });
        return;
      }

      setEmail('');
      setSubscription({
        state: 'success',
        message: result.message ?? 'Thanks for subscribing.',
      });
    } catch {
      setSubscription({
        state: 'error',
        message: 'We could not subscribe you. Please try again.',
      });
    }
  };

  const [products, tools, company, legal, calculators] = footerColumns;

  return (
    // Webflow `.footer-top`: z-index 3 so the homepage card hand (z-1 +
    // translateY 20rem) never paints over Subscribe / nav columns.
    <footer className={cn('relative z-[3] bg-white', className)}>
      <Container className="pt-20 md:pt-32">
        <Grid className="gap-y-14">
          {/* md: email [1–2] | empty [3] | Products [4–5] | Tools [6–7] | Calculators [8–9] */}
          <div className="col-span-9 h-fit md:col-span-2 md:col-start-1">
            <form
              onSubmit={onSubscribe}
              className="flex items-center gap-3 border-b border-black-6 pb-3 transition-colors focus-within:border-grey-900">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (subscription.state !== 'idle') setSubscription({ state: 'idle' });
                }}
                required
                autoComplete="email"
                placeholder="Enter your email"
                aria-label="Email address"
                aria-describedby="newsletter-status"
                disabled={subscription.state === 'submitting'}
                className="w-full bg-transparent text-body-m text-ink outline-none placeholder:text-grey-400 disabled:opacity-60"
              />
              <input
                type="text"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />
              <button
                type="submit"
                disabled={subscription.state === 'submitting'}
                aria-busy={subscription.state === 'submitting'}
                className="shrink-0 text-body-m font-medium text-red-500 transition-colors hover:text-red-700 disabled:cursor-wait disabled:text-grey-400">
                {subscription.state === 'submitting' ? 'Subscribing…' : 'Subscribe'}
              </button>
            </form>
            <p
              id="newsletter-status"
              role="status"
              aria-live="polite"
              className={cn(
                'mt-2 min-h-[1.25rem] text-body-s',
                subscription.state === 'error' ? 'text-red-700' : 'text-grey-700',
              )}>
              {subscription.message}
            </p>
          </div>

          {/* Explicit empty track so column 3 cannot be auto-filled */}
          <div
            aria-hidden="true"
            className="pointer-events-none col-span-9 hidden md:col-span-1 md:col-start-3 md:block"
          />

          <div className="col-span-9 grid grid-cols-2 gap-x-5 gap-y-18 md:col-span-2 md:col-start-4 md:grid-cols-1">
            <FooterNav column={products} />
            <FooterNav column={company} />
          </div>

          <div className="col-span-9 grid grid-cols-2 gap-x-5 gap-y-18 md:col-span-2 md:col-start-6 md:grid-cols-1">
            <FooterNav column={tools} />
            <FooterNav column={legal} />
          </div>

          <div className="col-span-9 grid grid-cols-2 gap-x-5 gap-y-18 md:col-span-2 md:col-start-8 md:grid-cols-1">
            <FooterNav column={calculators} />
          </div>
        </Grid>
        <LocaleSelector />
      </Container>

      <div className="mt-24 bg-grey-25">
        <Container className="space-y-4 py-12 text-body-s text-grey-700">
          <p>
            Copyright © {new Date().getFullYear()} nsave Ltd. All rights reserved.
            <br />
            This website is owned and operated by nsave Ltd., incorporated in the United Kingdom.
            The information provided is not legally binding and does not constitute an offering,
            endorsement, recommendation or solicitation to enter into any type of financial
            transaction in this or in any other jurisdiction in which such solicitation or offer
            would be unlawful under the laws of such jurisdiction.
          </p>
          <p>
            npay Inc. is registered in the United States in the State of Delaware. npay Inc.
            partners with licensed financial institutions and is a technology services provider, not
            a bank.
          </p>
          <p>
            npay USA Inc. is incorporated in Montana, USA and registered as a Money Services
            Business (“MSB”) with the Financial Crimes Enforcement Network (“FinCEN”) under MSB
            Registration Number 31000315267589
          </p>
          <p>
            GBP and Multi-Currency accounts are offered by Keel Money Ltd; an Electronic Money
            Institution authorised by the Financial Conduct Authority under the Electronic Money
            Regulations 2011 (FRN 1020783). Client funds are safeguarded in UK- or EEA-authorised
            credit institutions but are not protected by the Financial Services Compensation Scheme.
          </p>
          <p>
            nsave ltd is appointed under Regulation 33 of the EMRs to distribute and/or redeem
            electronic money on behalf of Keel Money Ltd and is not itself authorised to issue
            electronic money or provide payment services. More details can be found in the Keel
            End-User T&Cs, which you must agree to before using any services provided by Keel Money
            Ltd.
          </p>
        </Container>
      </div>
    </footer>
  );
}
